// Libraries
import { useEffect, useState } from "react";
import clsx from "clsx";
import { GridAlignment, GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";

// Helpers
import { determineRowHeight, getTreeDataPath, isItChild, isItDrawText, isItLastDraw, isItOverDraw, transformNewRows } from "../helpers";

// Components
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import { prepareGroupColDef } from "@/Layouts/components/TreeGrid/prepareGroupColDef";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";

// Constants
import { tableKeys } from "@/Constants/tableKeys";
import { ONE_HUNDRED_FIFTEEN, TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";
import { DOLLAR_SYMBOL_CLASSNAME, HEADER_CUSTOM_CLASS_NAME } from "../constants";

// Types
import { TNewDrawRow } from "../types";

type TDrawDepartmentTableProps = {
	department: string;
	position: string;
	data: any[];
};

export default function DrawTable(props: Readonly<TDrawDepartmentTableProps>) {
	const { data, department, position } = props;

	const apiRef = useGridApiRef();

	const [rows, setRows] = useState<TNewDrawRow[]>([]);

	const groupingColDef = {
		...prepareGroupColDef({ expandedAll: false }),
		cellClassName: "!border-none",
	};

	const sameProperties = (align: GridAlignment = "right"): Partial<GridColDef> => ({
		align,
		flex: 1,
		resizable: false,
		filterable: true,
		headerClassName: HEADER_CUSTOM_CLASS_NAME,
	});

	const columns: GridColDef[] = [
		{
			field: "_id",
			headerName: "ID",
			filterable: false,
		},
		{
			field: "fullName",
			align: "left",
			headerName: "Associate Name",
			...sameProperties("left"),
			renderHeader: (params) => <TypographyHeader params={params} />,
			renderCell: (params) => {
				const containsWord = params.id.toString().includes("draw_") || params.id.toString().includes("last_row_draw_");
				if (containsWord) {
					const [firstText = "", secondText = ""] = params.value.split(" ") ?? [];
					return (
						<TypographyHeader className="!pt-2">
							{firstText}
							<br />
							{secondText}
						</TypographyHeader>
					);
				}

				return (
					<div className="flex items-center">
						<CustomCell value={params.value} hasTooltip maxWith={params.colDef.width ?? ONE_HUNDRED_FIFTEEN} fontSize="12px" />
					</div>
				);
			},
		},
		{
			field: "drawAmount",
			headerName: "Draw Amount",
			align: "right",
			type: "number",
			...sameProperties(),
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: (params) => {
				return (params?.value || !params?.row.draws?.length) && !isItDrawText(params?.row._id) && !isItLastDraw(params?.row._id)
					? DOLLAR_SYMBOL_CLASSNAME
					: "";
			},
			renderCell: (params) => {
				const isOverDraw = isItOverDraw(String(params.id));

				if (isOverDraw) {
					return "";
				}

				if (!params.value) {
					const value = "-";
					return (
						<TypographyCell
							formatOptions={{ decimals: 2 }}
							hints={{ isFunded: false }}
							params={params}
							issues={[]}
							render="account"
							fontSize={"12px"}
						>
							{value}
						</TypographyCell>
					);
				}
				return (
					<TypographyCell
						params={params}
						formatOptions={{ decimals: 2 }}
						issues={[]}
						hints={{ isFunded: false }}
						fontSize={"12px"}
						render="account"
					/>
				);
			},
		},
		{
			field: "overdraw",
			headerName: "Overdraw Amount",
			...sameProperties(),
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: (params) => {
				return params?.value ? DOLLAR_SYMBOL_CLASSNAME : "";
			},
			renderCell: (params) => {
				const isEmptyValue = isItOverDraw(String(params.id)) || isItChild(String(params.id));

				if (isEmptyValue) {
					return "";
				}

				if (!params.value || params.value === "0.00") {
					const value = "-";
					return (
						<TypographyCell
							formatOptions={{ decimals: 2 }}
							hints={{ isFunded: false }}
							params={params}
							issues={[]}
							render="account"
							fontSize={"12px"}
						>
							{value}
						</TypographyCell>
					);
				}
				return (
					<TypographyCell
						params={params}
						formatOptions={{ decimals: 2 }}
						issues={[]}
						hints={{ isFunded: false }}
						fontSize={"12px"}
						render="account"
					/>
				);
			},
		},
	];

	// Generate unique filter key for each position within each department
	const filterKey = `${tableKeys.DATA_PAGES.DRAWS_AND_OVERDRAWS}_${department}_${position}`;

	useEffect(() => {
		if (!data?.length) {
			setRows([]);
			return;
		}

		const newRows = transformNewRows(data);

		setRows(newRows);
	}, [data]);

	return (
		<DataSmartTable
			filterStoreConfig={{
				key: filterKey,
			}}
			apiRef={apiRef}
			treeData
			rows={rows}
			columns={columns}
			maxRows={10}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			columnVisibilityModel={{
				_id: false,
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					titleExpandible: {
						className: "[&_.toolbar-actions]:!right-0 [&_.toolbar-actions]:!bottom-0",
						hide: true,
					},
					filter: {
						hide: false,
					},
					search: {
						useQuickFilter: true,
					},
				},
				noRowsOverlay: {
					title: "No Information Data",
				},
			}}
			hideFooter
			customProps={{
				tableContainerStyle: { height: "100%", width: "100%" },
				tableClassName: {
					...TAILWIND_FOR_DATA_SMART_TABLE,
					"[&_.toolbar-actions]:!top-[0px]": true,
					"[&_.MuiDataGrid-main]:mt-[16px]": true,
				},
				hide: false,
				expandableRows: true,
			}}
			enableRowsFiller={true}
			getTreeDataPath={getTreeDataPath}
			groupingColDef={groupingColDef}
			getRowClassName={(params) => {
				const isDrawText = isItDrawText(String(params.id));
				const isLastRow = isItLastDraw(String(params.id));

				return clsx({
					"[&_.MuiDataGrid-cell]:!bg-white [&_.MuiDataGrid-scrollbarFiller]:!bg-white": isDrawText || isLastRow,
					"[&_.MuiDataGrid-cell]:!shadow-[inset_0_-1px_0_0_#CCC] [&_.MuiDataGrid-scrollbarFiller]:!shadow-[inset_0_-1px_0_0_#CCC]":
						isDrawText,
					"[&_.MuiDataGrid-cell]:!bg-[#F5F5F5] [&_.MuiDataGrid-scrollbarFiller]:!bg-[#F5F5F5]": false,
					"[&_.MuiDataGrid-cell]:!bg-red-500 [&_.MuiDataGrid-scrollbarFiller]:!bg-red-500": false,
				});
			}}
			getRowHeight={determineRowHeight}
		/>
	);
}
